import type { ConvexClient } from "convex/browser";
import { makeFunctionReference } from "convex/server";
import { useCallback, useEffect, useRef } from "react";

import { fetchPrimary } from "~/environments/primary/fetch";

import { useEnvironments, usePrimaryEnvironmentId } from "../state/environments";
import { useWorldWorkspaceBindings, type WorldWorkspaceBindings } from "./worldWorkspaceBinding";
import { useWorldConvexClient } from "./worldConvexClient";
import { useNeucleosWorldStore } from "./worldStore";

// Step 3 of the one-backend plan (increment 2): the Desk's + on a Workroom starts its session in the World, as a
// conversation kept there (cleoConversations, the same one Pocket and the web see), where the World's switch says so
// (backend/convex/deskConversationRoute.js, DESK_WORLD_CONVERSATION_WORLDS). Everywhere else it is the Mac's door, as
// before. The World route is taken only when every one of these holds at the press, and the Mac's door otherwise:
// - the World answered "world" for this window's own client and this World (a live answer kept here; a press never
//   waits for it, so a slow, lost or refused answer is the Mac's door);
// - this Desk names no folder for that World, read from every environment's own settings once all have arrived: any folder,
//   Files root or project it names, even a broken one, keeps the Mac's grounding (WorldSessionFirstExecutionLauncher).
//   An emptied list names none (the Mac's launcher reads it as unbound too), so it does not. EXCEPT (steps 5-7 design §2.3,
//   step 6.1): when the folders are this Mac's own explicit choices for the World ("explicit-here"), the World's Mac tools
//   are on for it, and this Mac is the one the person turned on for it ("Let Cleo use this Mac from Pocket and the web"),
//   the conversation starts in the World and uses this Mac's folders through it (its Mac is recorded on it at birth);
// - the Workroom is already in the World's index (a Workroom not made yet is made by the Mac's room door, which also
//   reconciles one another screen made unseen);
// - the press is a New (Resume reads the room's latest root, which only the Mac's door asks the World for).

export type DeskConversationRoute = "world" | "mac";

const routeRef = makeFunctionReference<
  "query",
  { worldKey: string },
  { route: DeskConversationRoute; macTools?: boolean }
>("deskConversationRoute:read");

/** The World's answer, as a route: only an exact "world" is the World's. Pure. */
export const routeOfAnswer = (answer: unknown): DeskConversationRoute =>
  typeof answer === "object" && answer !== null && (answer as { route?: unknown }).route === "world"
    ? "world"
    : "mac";

/** Whether the World's answer says its Mac tools are on (an exact true, with the World's route). Pure. */
export const macToolsOfAnswer = (answer: unknown): boolean =>
  routeOfAnswer(answer) === "world" && (answer as { macTools?: unknown }).macTools === true;

const named = (value: unknown): boolean => typeof value === "string" && value.trim().length > 0;

/**
 * What this Desk names for the World (steps 5-7 design §2.3 as amended by §8 R6, §9 N9). Pure:
 * - "unknown" until every environment's own settings have arrived (one still loading may hold the World's folder);
 * - "none" when no environment's entry for that World names a source folder, a folder, a default project (found or not)
 *   or a Files root, an emptied list included;
 * - "explicit-here" when the binding a session would be grounded in, as the launcher picks it (worldWorkspaceBinding.ts
 *   resolveWorldSessionLaunchGrounding: the first environment that writes sourceFolders, else the first that writes a folder
 *   list), is this Mac's own (`primaryEnvironmentId`) and names at least one folder, and no other environment names any:
 *   the folders a conversation kept in the World reads on this Mac are exactly these (cloudFolderContext.ts reads the same
 *   lists; a legacy pointer beside them is ignored by both);
 * - "other" for everything else: only a legacy project or Files root, another environment's folders, an emptied explicit
 *   list beside a legacy pointer. Those keep the Mac's door until the person saves their folders again in Settings.
 */
export type DeskFolderSetting = "none" | "unknown" | "explicit-here" | "other";

export function deskFolderSettingFor(input: {
  readonly worldKey: string;
  readonly bindingsByEnvironment: ReadonlyMap<string, WorldWorkspaceBindings>;
  readonly primarySettingsArrived: boolean;
  /** This Mac's own environment (the Desk's own server); absent, nothing is this Mac's. */
  readonly primaryEnvironmentId?: string | null;
}): DeskFolderSetting {
  if (!input.primarySettingsArrived) return "unknown";
  type Binding = WorldWorkspaceBindings[string];
  const entries: Array<readonly [string, Binding]> = [];
  for (const [environmentId, bindings] of input.bindingsByEnvironment) {
    const binding = bindings[input.worldKey];
    if (binding !== undefined) entries.push([environmentId, binding]);
  }
  const listed = (binding: Binding): ReadonlyArray<string> =>
    binding.sourceFolders !== undefined
      ? [...binding.sourceFolders.codebase, ...binding.sourceFolders.files]
      : (binding.folders ?? []);
  const namesAny = (binding: Binding) =>
    [...(binding.sourceFolders?.codebase ?? []), ...(binding.sourceFolders?.files ?? [])].some(
      named,
    ) ||
    (binding.folders ?? []).some(named) ||
    binding.defaultExecutionProjectId !== undefined ||
    named(binding.filesRoot);
  if (!entries.some(([, binding]) => namesAny(binding))) return "none";
  const grounding =
    entries.find(([, binding]) => binding.sourceFolders !== undefined) ??
    entries.find(([, binding]) => binding.folders !== undefined);
  if (grounding === undefined) return "other";
  const [environmentId, binding] = grounding;
  if (
    input.primaryEnvironmentId == null ||
    environmentId !== input.primaryEnvironmentId ||
    !listed(binding).some(named) ||
    entries.some(([other, candidate]) => other !== environmentId && namesAny(candidate))
  )
    return "other";
  return "explicit-here";
}

/** This Mac for the World's Mac tools: the switch is on and this Mac is the person's (its name in the World), or not. */
export interface DeskMacTools {
  readonly on: boolean;
  readonly thisMac: string | null;
}

/**
 * The one rule: does this press start its session in the World? A New, on the World's answer, for a Workroom already made,
 * with no folder named, or with this Mac's own explicit folders while the World's Mac tools are on and this Mac is the one the
 * person turned on for it (design §2.3). Pure.
 */
export function deskBirthGoesToWorld(input: {
  readonly intent: "resume" | "new";
  readonly route: DeskConversationRoute;
  readonly folderSetting: DeskFolderSetting;
  readonly workroomKnown: boolean;
  readonly macTools?: DeskMacTools | undefined;
}): boolean {
  if (input.intent !== "new" || input.route !== "world" || !input.workroomKnown) return false;
  if (input.folderSetting === "none") return true;
  return (
    input.folderSetting === "explicit-here" &&
    input.macTools?.on === true &&
    input.macTools.thisMac !== null
  );
}

/**
 * Whether the World's route can change where a birth goes (deskBirthGoesToWorld): not for a resume, a Workroom the index does
 * not hold, or a Desk that names another folder for the World; those are the Mac's whatever the World answers, so their press
 * never waits for the answer (W-3f25c9ff, Codex review r1 of #3183). Pure.
 */
export function deskBirthRouteMatters(input: {
  readonly intent: "resume" | "new";
  readonly folderSetting: DeskFolderSetting;
  readonly workroomKnown: boolean;
}): boolean {
  return input.intent === "new" && input.workroomKnown && input.folderSetting !== "other";
}

/** What a World birth records on its conversation: this Mac, for a folder conversation; nothing otherwise. Pure. */
export function deskBirthMacTools(input: {
  readonly folderSetting: DeskFolderSetting;
  readonly macTools?: DeskMacTools | undefined;
}): { readonly mac: string } | undefined {
  return input.folderSetting === "explicit-here" &&
    input.macTools?.on === true &&
    input.macTools.thisMac !== null
    ? { mac: input.macTools.thisMac }
    : undefined;
}

/**
 * Whether the World refused a folder birth because the conversation cannot be this Mac's (backend cleoConversations
 * BIRTH_MAC_REFUSED). The conversation it may have opened stays an ordinary one of the person's; the next press starts afresh
 * (a new key, new coordinates), so a refused folder birth never strands the + (Codex review r2 of #2726).
 */
export const isBirthMacRefusal = (reason: string): boolean =>
  reason.includes("can't use this Mac's folders");

/** The window's event after the person presses "Let Cleo use this Mac from Pocket and the web" (MacForToolsSwitch.tsx). */
export const MAC_FOR_TOOLS_CHANGED_EVENT = "cleo:mac-for-tools-changed";

/** A failed read of this Mac's switch is tried again after this, doubling up to THIS_MAC_RETRY_MAX_MS. */
export const THIS_MAC_RETRY_FIRST_MS = 1_000;
export const THIS_MAC_RETRY_MAX_MS = 15_000;
/** The longest a folder birth's press waits for this Mac's switch when it has not been read yet. */
export const THIS_MAC_PRESS_WAIT_MS = 3_000;
/**
 * The longest a press waits for the World's first route answer when it has not come yet (W-3f25c9ff): right after the Desk
 * opens the World can take seconds to answer (its first draw took ~20 s on 23 Oct 2026, W-b853d3b9), and a press that
 * decided at once started a Mac session in a World that keeps its conversations. A World that has not answered by then
 * starts nothing: the press says so (ROUTE_UNANSWERED_REASON) rather than falling back to a Mac session (the lead, 23 Oct).
 */
export const ROUTE_PRESS_WAIT_MS = 15_000;
/**
 * A press's settled route (useDeskConversationRoute routeSettledAt): the World's answer with the client it came through (a
 * World birth uses exactly that client), or "unanswered" / "cancelled", where the press starts nothing.
 */
export type SettledRoute =
  | { readonly route: DeskConversationRoute; readonly client: ConvexClient | null }
  | "unanswered"
  | "cancelled";

/** What a press says when the World has not answered where its conversations live within ROUTE_PRESS_WAIT_MS. */
export const ROUTE_UNANSWERED_REASON =
  "Cleo could not reach your World to start this conversation yet. Nothing was started; press + again in a moment.";

/**
 * This Mac's switch for the World, from the Desk's own server (macForToolsHttp.ts); null when it cannot say, "refused" when
 * the World refused this Mac for the World (409 not_available): asking again on a timer only adds refusals, so the hook
 * then waits for the person (the window's focus, or the switch pressed). No sign-in yet (404) is still retried: a sign-in
 * that is finishing answers it.
 */
export async function readThisMac(
  worldKey: string,
): Promise<{ readonly on: boolean; readonly mac: string } | "refused" | null> {
  const response = await fetchPrimary(
    `/api/neucleos/mac-for-tools?worldKey=${encodeURIComponent(worldKey)}`,
  ).catch(() => null);
  if (response?.status === 409) return "refused";
  if (!response?.ok) return null;
  const body = (await response.json().catch(() => null)) as { on?: unknown; mac?: unknown } | null;
  return typeof body?.on === "boolean" && typeof body.mac === "string" && body.mac.length > 0
    ? { on: body.on, mac: body.mac }
    : null;
}

/**
 * The key of one press's conversation. A press whose conversation the World opened but which then failed (its read-back
 * refused, the sign-in changed) keeps its key, so pressing again reaches that same conversation (openOrCreate answers the
 * same one for the same key) instead of making a second; a success, or a failure before the World opened anything,
 * lets it go. One key per World, Workroom and person.
 */
export function createBirthKeys(mint: () => string) {
  const keys = new Map<string, { readonly identity: string | null; readonly clientKey: string }>();
  const slotOf = (worldKey: string, workroomKey: string) => `${worldKey}\u0000${workroomKey}`;
  return {
    take(worldKey: string, workroomKey: string, identity: string | null): string {
      const slot = slotOf(worldKey, workroomKey);
      const kept = keys.get(slot);
      if (kept !== undefined && kept.identity === identity) return kept.clientKey;
      const clientKey = mint();
      keys.set(slot, { identity, clientKey });
      return clientKey;
    },
    settle(
      worldKey: string,
      workroomKey: string,
      outcome: { readonly ok: boolean; readonly opened: boolean },
    ): void {
      if (outcome.ok || !outcome.opened) keys.delete(slotOf(worldKey, workroomKey));
    },
  };
}

/**
 * What a press needs to decide: the World's last answer for this window's client and World ("mac" until there is one,
 * on any error, and for any other client or World), and this Desk's folder setting for that World.
 */
export function useDeskConversationRoute(worldKey: string | null): {
  readonly routeAt: (client: ConvexClient | null, worldKey: string) => DeskConversationRoute;
  readonly folderSettingAt: (worldKey: string) => DeskFolderSetting;
  readonly macToolsAt: (client: ConvexClient | null, worldKey: string) => DeskMacTools;
  readonly macToolsSettledAt: (
    client: ConvexClient | null,
    worldKey: string,
  ) => Promise<DeskMacTools>;
  readonly routeSettledAt: (worldKey: string) => Promise<SettledRoute>;
} {
  const client = useWorldConvexClient();
  const latest = useRef<{
    readonly client: ConvexClient;
    readonly worldKey: string;
    readonly route: DeskConversationRoute;
    readonly macTools: boolean;
  } | null>(null);
  // W-3f25c9ff: the client and World this window listens with now, and the presses waiting for the World's answer
  // (routeSettledAt), woken on every answer and every change of client or World. A press decides by the person and the World,
  // never by which client object answers: a client replaced for the same person keeps it waiting (Codex review r2 of #3183).
  const listening = useRef<{
    readonly client: ConvexClient | null;
    readonly worldKey: string | null;
  }>({
    client: null,
    worldKey: null,
  });
  const waiters = useRef(new Set<() => void>());
  const wake = useCallback(() => {
    const woken = [...waiters.current];
    waiters.current.clear();
    for (const waiter of woken) waiter();
  }, []);
  useEffect(() => {
    latest.current = null;
    listening.current = { client, worldKey };
    wake();
    if (client === null || worldKey === null)
      return () => {
        listening.current = { client: null, worldKey: null };
        wake();
      };
    let stopped = false;
    let unsubscribe: (() => void) | undefined;
    try {
      unsubscribe = client.onUpdate(
        routeRef,
        { worldKey },
        (answer) => {
          if (stopped) return;
          latest.current = {
            client,
            worldKey,
            route: routeOfAnswer(answer),
            macTools: macToolsOfAnswer(answer),
          };
          wake();
        },
        () => {
          if (stopped) return;
          latest.current = { client, worldKey, route: "mac", macTools: false };
          wake();
        },
      );
    } catch {
      unsubscribe = undefined;
    }
    return () => {
      stopped = true;
      latest.current = null;
      listening.current = { client: null, worldKey: null };
      wake();
      unsubscribe?.();
    };
  }, [client, worldKey, wake]);
  const routeAt = useCallback((at: ConvexClient | null, key: string): DeskConversationRoute => {
    const kept = latest.current;
    return at !== null && kept !== null && kept.client === at && kept.worldKey === key
      ? kept.route
      : "mac";
  }, []);

  // This Mac's own switch for the World, read from the Desk's server when the World changes, when the window comes back, and when
  // the person presses the switch; a press never waits for it (off until it is read). Answers are taken in the order they were
  // asked (Codex review r1 of #2726, P2 4): an older read that comes back late never replaces a newer one, and a new World or
  // sign-in forgets the last answer. The World checks again at the birth (cleoConversations.recordBirthMac).
  //
  // A read that fails (the Desk's server not ready to answer yet, as right after the Desk opens) is tried again on its own, 1 s
  // later and then doubling up to 15 s, until it is answered: it used to wait for the window's next focus, and until then a folder
  // Desk's + started a Mac session (the Tester's Can 50 folders-half run on #2747).
  const thisMac = useRef<{
    readonly worldKey: string;
    readonly on: boolean;
    readonly mac: string;
  } | null>(null);
  const reads = useRef(0);
  /** The newest read in flight, for a press that waits for it (macToolsSettledAt). */
  const reading = useRef<{
    readonly worldKey: string;
    readonly done: Promise<void>;
    answered: boolean;
  } | null>(null);
  /** Starts a read now (the hook's own refresh while it listens); null when it does not listen. */
  const readNow = useRef<(() => void) | null>(null);
  useEffect(() => {
    thisMac.current = null;
    reading.current = null;
    if (worldKey === null) return;
    let stopped = false;
    let retry: ReturnType<typeof setTimeout> | undefined;
    let backoffMs = THIS_MAC_RETRY_FIRST_MS;
    const refresh = () => {
      if (retry !== undefined) clearTimeout(retry);
      retry = undefined;
      const asked = ++reads.current;
      const attempt: { worldKey: string; done: Promise<void>; answered: boolean } = {
        worldKey,
        done: Promise.resolve(),
        answered: false,
      };
      attempt.done = readThisMac(worldKey).then((read) => {
        if (stopped || asked !== reads.current) return;
        thisMac.current = read === null || read === "refused" ? null : { worldKey, ...read };
        // Read, or refused by the World: no retry; the window's focus or a press reads again.
        if (read !== null) {
          backoffMs = THIS_MAC_RETRY_FIRST_MS;
          return;
        }
        retry = setTimeout(refresh, backoffMs);
        backoffMs = Math.min(backoffMs * 2, THIS_MAC_RETRY_MAX_MS);
      });
      void attempt.done.then(() => {
        attempt.answered = true;
      });
      reading.current = attempt;
    };
    readNow.current = refresh;
    refresh();
    window.addEventListener("focus", refresh);
    window.addEventListener(MAC_FOR_TOOLS_CHANGED_EVENT, refresh);
    return () => {
      stopped = true;
      if (retry !== undefined) clearTimeout(retry);
      thisMac.current = null;
      reading.current = null;
      readNow.current = null;
      window.removeEventListener("focus", refresh);
      window.removeEventListener(MAC_FOR_TOOLS_CHANGED_EVENT, refresh);
    };
  }, [worldKey, client]);
  const macToolsAt = useCallback((at: ConvexClient | null, key: string): DeskMacTools => {
    const kept = latest.current;
    const mine = thisMac.current;
    const switchOn =
      at !== null && kept !== null && kept.client === at && kept.worldKey === key && kept.macTools;
    return switchOn && mine !== null && mine.worldKey === key && mine.on
      ? { on: true, thisMac: mine.mac }
      : { on: false, thisMac: null };
  }, []);
  // A folder birth's press, when the World's Mac tools are on and this Mac's switch has not been read yet: it waits for one read
  // (the one in flight, or a new one), at most THIS_MAC_PRESS_WAIT_MS, then decides as macToolsAt does. Any other press: at once.
  const macToolsSettledAt = useCallback(
    async (at: ConvexClient | null, key: string): Promise<DeskMacTools> => {
      const kept = latest.current;
      const switchOn =
        at !== null &&
        kept !== null &&
        kept.client === at &&
        kept.worldKey === key &&
        kept.macTools;
      if (switchOn && thisMac.current?.worldKey !== key) {
        // Only a read still in flight is waited for; one that has come back (and failed) is asked again now.
        if (reading.current?.worldKey !== key || reading.current.answered) readNow.current?.();
        const done = reading.current?.worldKey === key ? reading.current.done : null;
        if (done !== null) {
          let timer: ReturnType<typeof setTimeout> | undefined;
          await Promise.race([
            done,
            new Promise<void>((resolve) => {
              timer = setTimeout(resolve, THIS_MAC_PRESS_WAIT_MS);
            }),
          ]);
          if (timer !== undefined) clearTimeout(timer);
        }
      }
      return macToolsAt(at, key);
    },
    [macToolsAt],
  );

  const bindingsByEnvironment = useWorldWorkspaceBindings();
  const { environments } = useEnvironments();
  const primaryEnvironmentId = usePrimaryEnvironmentId();
  // The primary's own settings, and every other environment's: one still loading may hold the World's folder.
  const primarySettingsArrived =
    primaryEnvironmentId !== null &&
    environments.some((environment) => environment.environmentId === primaryEnvironmentId) &&
    environments.every((environment) => environment.serverConfig?.settings !== undefined);
  const folders = useRef({ bindingsByEnvironment, primarySettingsArrived, primaryEnvironmentId });
  folders.current = { bindingsByEnvironment, primarySettingsArrived, primaryEnvironmentId };
  const folderSettingAt = useCallback(
    (key: string) => deskFolderSettingFor({ worldKey: key, ...folders.current }),
    [],
  );
  // W-3f25c9ff: a press waits for the World's answer, from whichever client this window listens with, at most ROUTE_PRESS_WAIT_MS,
  // and follows it (an error is an answer: the Mac's door), returning the client the answer came through so a World birth uses
  // exactly that one; a press after it decides at once.
  // - Not signed in to a World: there is no World to ask; the Mac's door, as before.
  // - Signed in but the window's World client not published yet: it waits for it (Codex review r2 of #3183).
  // - Still unanswered at the deadline: "unanswered"; the press starts nothing and says so.
  // - The person changed (sign-in, or the World's Actor), the World changed, or the window stopped listening (the view gone)
  //   while it waited: "cancelled"; the press starts nothing. A client replaced for the same person and World is none of these:
  //   the press keeps waiting for its answer (Codex reviews r2 and r3 of #3183).
  const routeSettledAt = useCallback(async (key: string): Promise<SettledRoute> => {
    const personOf = () => {
      const state = useNeucleosWorldStore.getState();
      const actor = state.worlds?.find((world) => world.worldKey === key)?.actorId ?? null;
      return state.signedIn ? `${state.identity ?? ""}\u0000${actor ?? ""}` : null;
    };
    const atPress = personOf();
    if (atPress === null) return { route: "mac", client: null };
    const deadline = Date.now() + ROUTE_PRESS_WAIT_MS;
    for (;;) {
      if (personOf() !== atPress) return "cancelled";
      const on = listening.current;
      if (on.worldKey !== key) return "cancelled";
      const kept = latest.current;
      if (on.client !== null && kept !== null && kept.client === on.client && kept.worldKey === key)
        return { route: kept.route, client: kept.client };
      const left = deadline - Date.now();
      if (left <= 0) return "unanswered";
      await new Promise<void>((resolve) => {
        const done = () => {
          clearTimeout(timer);
          waiters.current.delete(done);
          resolve();
        };
        const timer = setTimeout(done, left);
        waiters.current.add(done);
      });
    }
  }, []);

  return { routeAt, folderSettingAt, macToolsAt, macToolsSettledAt, routeSettledAt };
}
